import { isOffcanvasExpanded, setOffcanvasWide } from '@/front/offcanvas';
import { onLoad } from '@/front/utils/dom';

const ACTIVE_SECTION_CLASSES = ['bg-primary-subtle', 'fw-semibold'];
const ACTIVE_ICON_CLASSES = ['text-primary'];
const ACTIVE_CARD_CLASSES = ['fw-semibold', 'text-primary', 'border-primary', 'border-2'];

const toggleClasses = (element: Element | null, classNames: string[], force: boolean) =>
  classNames.forEach((className) => element?.classList.toggle(className, force));

const getPanels = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>('[data-section-panel]')];

const isCardHidden = (wrapper: HTMLElement) => {
  const card = wrapper.querySelector('.card');

  return !card || card.closest('.d-none, [data-section-card]') !== wrapper;
};

const refreshCardLinks = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('[data-section-nav-item]').forEach((item) => {
    const panel = root.querySelector<HTMLElement>(`[data-section-panel="${item.dataset.sectionNavItem}"]`)!;

    let visibleCount = 0;

    item.querySelectorAll<HTMLElement>('[data-section-nav-card-link]').forEach((link) => {
      const wrapper = panel.querySelector<HTMLElement>(`[data-section-card="${link.dataset.sectionNavCardLink}"]`)!;
      const hidden = isCardHidden(wrapper);

      link.classList.toggle('d-none', hidden);

      if (!hidden) visibleCount++;
    });

    item
      .querySelector('[data-section-nav-cards]')
      ?.classList.toggle('d-none', panel.classList.contains('d-none') || visibleCount <= 1);
  });
};

const setActiveCardLink = (root: HTMLElement, cardId?: string) => {
  root.querySelectorAll<HTMLElement>('[data-section-nav-card-link]').forEach((link) => {
    toggleClasses(link, ACTIVE_CARD_CLASSES, link.dataset.sectionNavCardLink === cardId);
  });
};

const scrollToCard = (root: HTMLElement, cardId: string) => {
  setActiveCardLink(root, cardId);

  root.querySelector(`[data-section-card="${cardId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const showSection = (root: HTMLElement, id?: string | null) => {
  const panel = root.querySelector<HTMLElement>(`[data-section-panel="${id}"]`) ?? getPanels(root)[0];
  const sectionId = panel.dataset.sectionPanel!;

  getPanels(root).forEach((p) => p.classList.toggle('d-none', p !== panel));

  document
    .querySelectorAll('[data-action="expand"]')
    .forEach((button) => button.classList.toggle('d-none', !panel.dataset.sectionExpandable));

  root.querySelectorAll<HTMLElement>('[data-section-nav-link]').forEach((link) => {
    const active = link.dataset.sectionNavLink === sectionId;

    toggleClasses(link, ACTIVE_SECTION_CLASSES, active);
    toggleClasses(link.querySelector('i'), ACTIVE_ICON_CLASSES, active);
  });

  refreshCardLinks(root);

  const firstCardLink = root.querySelector<HTMLElement>(
    `[data-section-nav-item="${sectionId}"] [data-section-nav-card-link]:not(.d-none)`,
  );

  setActiveCardLink(root, firstCardLink?.dataset.sectionNavCardLink);
  window.scrollTo({ top: 0 });
};

onLoad(() => {
  const root = document.querySelector<HTMLElement>('[data-section-nav]');

  setOffcanvasWide(!!root);

  if (!root) return;

  root.querySelectorAll<HTMLAnchorElement>('[data-section-nav-link]').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      showSection(root, link.dataset.sectionNavLink!);
    });
  });

  root.querySelectorAll<HTMLAnchorElement>('[data-section-nav-card-link]').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      scrollToCard(root, link.dataset.sectionNavCardLink!);
    });
  });

  const observer = new MutationObserver(() => refreshCardLinks(root));

  getPanels(root).forEach((panel) => observer.observe(panel, { subtree: true, attributeFilter: ['class'] }));

  refreshCardLinks(root);

  const syncExpanded = () =>
    root.querySelector('[data-section-nav-column]')?.classList.toggle('d-none', isOffcanvasExpanded());

  syncExpanded();

  window.addEventListener('offcanvasExpandedChange', syncExpanded);

  showSection(root, window.location.hash.split('#')[1]);
});
